package com.isaias.fyattravel.web.flight

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.InputType
import com.isaias.fyattravel.web.layout.ResponsiveStyles
import com.isaias.fyattravel.web.i18n.WebStringKey
import com.isaias.fyattravel.web.i18n.WebStrings
import com.isaias.fyattravel.web.theme.ThemeColors
import com.isaias.fyattravel.web.components.EmailInputBox

private val EMAIL_REGEX = Regex("^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$")

/**
 * Simplified Date Picker Component with Calendar Popup
 */
@Composable
fun DatePickerField(
    label: String,
    selectedDate: String,
    onDateSelected: (String) -> Unit,
    placeholder: String = "",
    minDate: String? = null,
    maxDate: String? = null
) {
    var showCalendar by remember { mutableStateOf(false) }
    var currentMonth by remember { mutableStateOf(getCurrentMonth()) }
    var currentYear by remember { mutableStateOf(getCurrentYear()) }
    val placeholderText = if (placeholder.isBlank()) WebStrings.t(WebStringKey.FLIGHT_SELECT_DATE) else placeholder
    
    // Parse selected date
    val selectedDateParts = selectedDate.split("-").mapNotNull { it.toIntOrNull() }
    val selectedYear = selectedDateParts.getOrNull(0)
    val selectedMonth = selectedDateParts.getOrNull(1)
    val selectedDay = selectedDateParts.getOrNull(2)
    
    Div({
        style {
            position(Position.Relative)
        }
    }) {
        // Input field
        Div({
            style {
                cursor("pointer")
            }
            onClick { showCalendar = !showCalendar }
        }) {
            Label(null, {
                style {
                    fontSize(0.8.em)
                    color(ThemeColors.textSecondary)
                    marginBottom(0.25.em)
                    display(DisplayStyle.Block)
                }
            }) { Text(label) }
            
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(0.5.em)
                    padding(0.75.em)
                    borderRadius(0.5.em)
                    property("border", "1px solid ${ThemeColors.borderLight}")
                    backgroundColor(ThemeColors.surface)
                    fontWeight("600")
                    property("@media (max-width: 640px)", "{ padding: 0.6em; font-size: 0.9em; }")
                }
            }) {
                Span({ style { fontSize(1.2.em) } }) { Text("📅") }
                Span {
                    Text(
                        if (selectedDate.isNotBlank()) {
                            formatDateForDisplay(selectedDate)
                        } else {
                            placeholderText
                        }
                    )
                }
                Span({
                    style {
                        property("margin-left", "auto")
                        color(ThemeColors.textSecondary)
                    }
                }) { Text(if (showCalendar) "▲" else "▼") }
            }
        }
        
        // Calendar Popup
        if (showCalendar) {
            Div({
                style {
                    position(Position.Absolute)
                    top(100.percent)
                    left(0.px)
                    marginTop(0.5.em)
                    backgroundColor(ThemeColors.surface)
                    borderRadius(0.75.em)
                    property("box-shadow", "0 20px 60px ${ThemeColors.shadow}")
                    padding(1.25.em)
                    width(340.px)
                    property("z-index", "1000")
                    property("border", "1px solid ${ThemeColors.divider}")
                    property("@media (max-width: 640px)", "{ width: 100%; padding: 0.9em; }")
                }
            }) {
                // Header with month/year navigation
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        marginBottom(1.em)
                        paddingBottom(0.75.em)
                        property("border-bottom", "1px solid ${ThemeColors.border}")
                    }
                }) {
                    // Previous month button
                    Button({
                        style {
                            backgroundColor(ThemeColors.surface)
                            color(ThemeColors.textSecondary)
                            border(0.px)
                            padding(0.5.em, 0.75.em)
                            borderRadius(0.5.em)
                            cursor("pointer")
                            fontSize(1.em)
                            fontWeight("600")
                        }
                        onClick {
                            currentMonth--
                            if (currentMonth < 1) {
                                currentMonth = 12
                                currentYear--
                            }
                        }
                    }) { Text("◀") }
                    
                    // Month/Year display
                    Div({
                        style {
                            fontWeight("700")
                            fontSize(1.1.em)
                            color(ThemeColors.textPrimary)
                        }
                    }) {
                        Text(getMonthName(currentMonth) + " " + currentYear)
                    }
                    
                    // Next month button
                    Button({
                        style {
                            backgroundColor(ThemeColors.surface)
                            color(ThemeColors.textSecondary)
                            border(0.px)
                            padding(0.5.em, 0.75.em)
                            borderRadius(0.5.em)
                            cursor("pointer")
                            fontSize(1.em)
                            fontWeight("600")
                        }
                        onClick {
                            currentMonth++
                            if (currentMonth > 12) {
                                currentMonth = 1
                                currentYear++
                            }
                        }
                    }) { Text("▶") }
                }
                
                // Day headers
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        gridTemplateColumns("repeat(7, 1fr)")
                        gap(0.25.em)
                        marginBottom(0.5.em)
                    }
                }) {
                    listOf("Su", "Mo", "Tu", "We", "Th", "Fr", "Sa").forEach { day ->
                        Div({
                            style {
                                textAlign("center")
                                fontSize(0.75.em)
                                color(ThemeColors.textMuted)
                                fontWeight("600")
                                padding(0.5.em)
                                property("text-transform", "uppercase")
                            }
                        }) { Text(day) }
                    }
                }
                
                // Calendar days
                val daysInMonth = getDaysInMonth(currentYear, currentMonth)
                val firstDayOfMonth = getFirstDayOfMonth(currentYear, currentMonth)
                
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        gridTemplateColumns("repeat(7, 1fr)")
                        gap(0.25.em)
                    }
                }) {
                    // Empty cells for days before month starts
                    repeat(firstDayOfMonth) {
                        Div {}
                    }
                    
                    // Days
                    for (day in 1..daysInMonth) {
                        val dateString = buildDateString(currentYear, currentMonth, day)
                        val isSelected = dateString == selectedDate
                        val isToday = dateString == getTodayDate()
                        val isDisabled = isDateDisabled(dateString, minDate, maxDate)
                        
                        Button({
                            style {
                                width(100.percent)
                                property("aspect-ratio", "1")
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                borderRadius(0.5.em)
                                border(0.px)
                                cursor(if (isDisabled) "not-allowed" else "pointer")
                                backgroundColor(when {
                                    isSelected -> ThemeColors.primary
                                    isToday -> ThemeColors.primaryMuted
                                    else -> Color.transparent
                                })
                                color(when {
                                    isSelected -> ThemeColors.textOnGold
                                    isDisabled -> ThemeColors.borderLight
                                    isToday -> ThemeColors.primary
                                    else -> ThemeColors.textSecondary
                                })
                                fontWeight(if (isSelected || isToday) "700" else "500")
                                fontSize(0.9.em)
                                opacity(if (isDisabled) 0.4 else 1.0)
                            }
                            onClick {
                                if (!isDisabled) {
                                    onDateSelected(dateString)
                                    showCalendar = false
                                }
                            }
                        }) {
                            Text(day.toString())
                        }
                    }
                }
                
                // Quick suggestions
                Div({
                    style {
                        marginTop(1.em)
                        paddingTop(1.em)
                        property("border-top", "1px solid ${ThemeColors.border}")
                    }
                }) {
                    Div({
                        style {
                            fontSize(0.75.em)
                            color(ThemeColors.textSecondary)
                            marginBottom(0.75.em)
                            fontWeight("600")
                            property("text-transform", "uppercase")
                            letterSpacing("0.05em")
                        }
                    }) { Text(WebStrings.t(WebStringKey.QUICK_SELECT)) }
                    
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            flexWrap(FlexWrap.Wrap)
                            gap(0.5.em)
                        }
                    }) {
                        getSuggestedDates().forEach { suggestion ->
                            Button({
                                style {
                                    backgroundColor(ThemeColors.surface)
                                    color(ThemeColors.textSecondary)
                                    border(0.px)
                                    padding(0.5.em, 0.75.em)
                                    borderRadius(0.5.em)
                                    cursor("pointer")
                                    fontSize(0.8.em)
                                    fontWeight("600")
                                }
                                onClick {
                                    onDateSelected(suggestion.date)
                                    showCalendar = false
                                }
                            }) {
                                Text(suggestion.label)
                            }
                        }
                    }
                }
            }
        }
    }
}

/**
 * Enhanced Flight Search Card with Date Pickers
 */
@Composable
fun EnhancedFlightSearchCard(
    onSearch: (FlightSearchParams) -> Unit = {}
) {
    var origin by remember { mutableStateOf("EBB") }
    var destination by remember { mutableStateOf("NBO") }
    var departureDate by remember { mutableStateOf(getTodayDate()) }
    var returnDate by remember { mutableStateOf("") }
    var tripType by remember { mutableStateOf(TripType.ROUND_TRIP) }
    var adults by remember { mutableStateOf(1) }
    var cabinClass by remember { mutableStateOf(CabinClass.ECONOMY) }
    var email by remember { mutableStateOf("") }
    var attemptedSend by remember { mutableStateOf(false) }
    val isEmailInvalid = attemptedSend && !EMAIL_REGEX.matches(email.trim())

    Div({
        style {
            backgroundColor(ThemeColors.surface)
            padding(1.75.em)
            borderRadius(1.em)
            property("box-shadow", "0 6px 18px ${ThemeColors.shadow}")
            marginBottom(1.5.em)
        }
    }) {
        // Trip Type Toggle
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(0.5.em)
                marginBottom(1.em)
                flexWrap(FlexWrap.Wrap)
                property("@media (max-width: 760px)", "{ gap: 0.4em; }")
            }
        }) {
            TripTypeButton(WebStrings.t(WebStringKey.FLIGHT_TAB_RETURN), tripType == TripType.ROUND_TRIP) {
                tripType = TripType.ROUND_TRIP
            }
            TripTypeButton(WebStrings.t(WebStringKey.FLIGHT_TAB_ONE_WAY), tripType == TripType.ONE_WAY) {
                tripType = TripType.ONE_WAY
            }
            TripTypeButton(WebStrings.t(WebStringKey.FLIGHT_TAB_MULTI_CITY), tripType == TripType.MULTI_CITY) {
                tripType = TripType.MULTI_CITY
            }
        }

        // Search Fields Grid
        Div({
            classes(ResponsiveStyles.flightSearchGrid)
            style {
                display(DisplayStyle.Grid)
                gap(1.em)
                alignItems(AlignItems.End)
                marginBottom(1.em)
            }
        }) {
            AirportInputField(
                WebStrings.t(WebStringKey.FLIGHT_FROM),
                origin,
                { origin = it },
                WebStrings.t(WebStringKey.FLIGHT_ORIGIN_PLACEHOLDER)
            )
            AirportInputField(
                WebStrings.t(WebStringKey.FLIGHT_TO),
                destination,
                { destination = it },
                WebStrings.t(WebStringKey.FLIGHT_DESTINATION_PLACEHOLDER)
            )

            NativeDateField(WebStrings.t(WebStringKey.FLIGHT_DEPARTURE), departureDate, getTodayDate()) {
                departureDate = it
            }

            if (tripType == TripType.ROUND_TRIP) {
                NativeDateField(WebStrings.t(WebStringKey.FLIGHT_RETURN), returnDate, departureDate) {
                    returnDate = it
                }
            } else {
                Div {}
            }

            EmailInputBox(WebStrings.t(WebStringKey.EMAIL_LABEL), email, isEmailInvalid) { email = it }

            Button({
                classes(ResponsiveStyles.buttonFullMobile)
                style {
                    property("background", "linear-gradient(135deg, #AE7930, #8B5E24)")
                    color(Color("#FFFFFF"))
                    padding(0.9.em, 1.5.em)
                    borderRadius(0.75.em)
                    border(0.px)
                    cursor("pointer")
                    fontWeight("700")
                    property("min-width", "120px")
                    property("box-shadow", "0 4px 14px rgba(139,94,36,0.35)")
                    marginTop(0.5.em)
                    property("transition", "all 0.25s ease")
                }
                onClick {
                    attemptedSend = true
                    if (!EMAIL_REGEX.matches(email.trim())) return@onClick
                    onSearch(FlightSearchParams(
                        origin = origin,
                        destination = destination,
                        departureDate = departureDate,
                        returnDate = if (tripType == TripType.ROUND_TRIP) returnDate else null,
                        adults = adults,
                        cabinClass = cabinClass,
                        tripType = tripType,
                        email = email.trim()
                    ))
                }
            }) { Text(WebStrings.t(WebStringKey.SEND_INQUIRY)) }
        }

        // Travelers and Cabin Class Row
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(1.em)
                alignItems(AlignItems.Center)
                flexWrap(FlexWrap.Wrap)
                property("@media (max-width: 760px)", "{ gap: 0.6em; }")
            }
        }) {
            Div({ style { position(Position.Relative) } }) {
                TravelersSelector(adults) { adults = it }
            }
            Div({ style { position(Position.Relative) } }) {
                CabinClassSelector(cabinClass) { cabinClass = it }
            }
        }
    }
}

@Composable
private fun TripTypeButton(
    label: String,
    active: Boolean,
    onClick: () -> Unit
) {
    Button({
        style {
            if (active) {
                property("background", "linear-gradient(135deg, #AE7930, #8B5E24)")
                color(Color("#FFFFFF"))
                property("box-shadow", "0 3px 10px rgba(174,121,48,0.30)")
            } else {
                backgroundColor(Color("#F2F2F2"))
                color(Color("#333333"))
            }
            padding(0.6.em, 1.em)
            borderRadius(0.5.em)
            border(0.px)
            cursor("pointer")
            fontWeight("600")
            fontSize(0.9.em)
            property("transition", "all 0.25s ease")
        }
        onClick { onClick() }
    }) {
        Text(label)
    }
}

@Composable
private fun NativeDateField(
    label: String,
    value: String,
    minDate: String? = null,
    onValueChange: (String) -> Unit
) {
    Div {
        Label(null, {
            style {
                fontSize(0.8.em)
                color(ThemeColors.textSecondary)
                marginBottom(0.25.em)
                display(DisplayStyle.Block)
            }
        }) { Text(label) }
        Input(type = InputType.Date, attrs = {
            this.value(value)
            onInput { onValueChange(it.value) }
            if (minDate != null) attr("min", minDate)
            style {
                width(100.percent)
                padding(0.75.em)
                borderRadius(0.5.em)
                property("border", "1px solid ${ThemeColors.borderLight}")
                backgroundColor(ThemeColors.surface)
                fontWeight("600")
                fontSize(0.95.em)
                property("box-sizing", "border-box")
            }
        })
    }
}

@Composable
private fun AirportInputField(
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String
) {
    Div {
        Label(null, {
            style {
                fontSize(0.8.em)
                color(ThemeColors.textSecondary)
                marginBottom(0.25.em)
                display(DisplayStyle.Block)
            }
        }) { Text(label) }
        
        Input(type = InputType.Text, attrs = {
            value(value)
            onInput { onValueChange(it.value.uppercase()) }
            style {
                width(100.percent)
                padding(0.75.em)
                borderRadius(0.5.em)
                property("border", "1px solid ${ThemeColors.borderLight}")
                backgroundColor(ThemeColors.surface)
                fontWeight("600")
                fontSize(1.em)
            }
            attr("placeholder", placeholder)
        })
    }
}

@Composable
private fun TravelersSelector(
    adults: Int,
    onAdultsChange: (Int) -> Unit
) {
    var showDropdown by remember { mutableStateOf(false) }
    
    Div({
        style {
            position(Position.Relative)
        }
    }) {
        Button({
            style {
                backgroundColor(ThemeColors.skeleton)
                color(ThemeColors.textSecondary)
                padding(0.6.em, 1.em)
                borderRadius(0.5.em)
                border(0.px)
                cursor("pointer")
                fontWeight("600")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(0.5.em)
            }
            onClick { showDropdown = !showDropdown }
        }) {
            Span { Text("👥") }
            Text("${adults} ${WebStrings.t(WebStringKey.FLIGHT_ADULTS)}")
        }
        
        if (showDropdown) {
            Div({
                style {
                    position(Position.Absolute)
                    top(100.percent)
                    left(0.px)
                    marginTop(0.5.em)
                    backgroundColor(ThemeColors.surface)
                    borderRadius(0.75.em)
                    property("box-shadow", "0 4px 12px rgba(0, 0, 0, 0.1)")
                    padding(1.em)
                    width(200.px)
                    property("z-index", "100")
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                    }
                }) {
                    Text(WebStrings.t(WebStringKey.FLIGHT_ADULTS))
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(0.5.em)
                        }
                    }) {
                        Button({
                            style {
                                width(32.px)
                                height(32.px)
                                borderRadius(50.percent)
                                backgroundColor(ThemeColors.skeleton)
                                border(0.px)
                                cursor("pointer")
                                fontWeight("700")
                            }
                            onClick { if (adults > 1) onAdultsChange(adults - 1) }
                        }) { Text("-") }
                        
                        Span({ 
                            style { 
                                fontWeight("700")
                                property("min-width", "24px")
                                textAlign("center")
                            } 
                        }) { Text(adults.toString()) }
                        
                        Button({
                            style {
                                width(32.px)
                                height(32.px)
                                borderRadius(50.percent)
                                backgroundColor(ThemeColors.skeleton)
                                border(0.px)
                                cursor("pointer")
                                fontWeight("700")
                            }
                            onClick { if (adults < 9) onAdultsChange(adults + 1) }
                        }) { Text("+") }
                    }
                }
            }
        }
    }
}

@Composable
private fun CabinClassSelector(
    selected: CabinClass,
    onSelect: (CabinClass) -> Unit
) {
    var showDropdown by remember { mutableStateOf(false) }
    
    Div({
        style {
            position(Position.Relative)
        }
    }) {
        Button({
            style {
                backgroundColor(ThemeColors.skeleton)
                color(ThemeColors.textSecondary)
                padding(0.6.em, 1.em)
                borderRadius(0.5.em)
                border(0.px)
                cursor("pointer")
                fontWeight("600")
            }
            onClick { showDropdown = !showDropdown }
        }) {
            Text(WebStrings.t(selected.key))
        }
        
        if (showDropdown) {
            Div({
                style {
                    position(Position.Absolute)
                    top(100.percent)
                    left(0.px)
                    marginTop(0.5.em)
                    backgroundColor(ThemeColors.surface)
                    borderRadius(0.75.em)
                    property("box-shadow", "0 4px 12px rgba(0, 0, 0, 0.1)")
                    padding(0.5.em)
                    width(180.px)
                    property("z-index", "100")
                }
            }) {
                CabinClass.values().forEach { cabin ->
                    Div({
                        style {
                            padding(0.75.em)
                            cursor("pointer")
                            borderRadius(0.5.em)
                            backgroundColor(if (cabin == selected) ThemeColors.primaryMuted else Color.transparent)
                            color(if (cabin == selected) ThemeColors.primary else ThemeColors.textSecondary)
                            fontWeight(if (cabin == selected) "700" else "400")
                        }
                        onClick { 
                            onSelect(cabin)
                            showDropdown = false
                        }
                    }) {
                        Text(WebStrings.t(cabin.key))
                    }
                }
            }
        }
    }
}

// Data Classes
enum class TripType { ONE_WAY, ROUND_TRIP, MULTI_CITY }
enum class CabinClass(val key: WebStringKey) {
    ECONOMY(WebStringKey.FLIGHT_CABIN_ECONOMY),
    PREMIUM_ECONOMY(WebStringKey.FLIGHT_CABIN_PREMIUM_ECONOMY),
    BUSINESS(WebStringKey.FLIGHT_CABIN_BUSINESS),
    FIRST(WebStringKey.FLIGHT_CABIN_FIRST)
}

data class AirlineInfo(
    val code: String,
    val name: String,
    val color: String,
    val iata: String
)

data class FlightSearchParams(
    val origin: String,
    val destination: String,
    val departureDate: String,
    val returnDate: String?,
    val adults: Int,
    val cabinClass: CabinClass,
    val tripType: TripType,
    val email: String
)

data class SuggestedDate(val date: String, val label: String)

// Helper Functions
private fun getCurrentMonth(): Int {
    val date = js("new Date()")
    return (date.getMonth() as Int) + 1
}

private fun getCurrentYear(): Int {
    val date = js("new Date()")
    return date.getFullYear() as Int
}

private fun getTodayDate(): String {
    val date = js("new Date()")
    val year = date.getFullYear() as Int
    val month = (date.getMonth() as Int) + 1
    val day = date.getDate() as Int
    return buildDateString(year, month, day)
}

private fun buildDateString(year: Int, month: Int, day: Int): String {
    val m = if (month < 10) "0$month" else month.toString()
    val d = if (day < 10) "0$day" else day.toString()
    return "$year-$m-$d"
}

private fun addDays(dateString: String, days: Int): String {
    val parts = dateString.split("-")
    if (parts.size != 3) return dateString
    
    val year = parts[0].toIntOrNull() ?: return dateString
    val month = parts[1].toIntOrNull() ?: return dateString
    val day = parts[2].toIntOrNull() ?: return dateString
    
    val date = js("new Date(year, month - 1, day)")
    date.setDate(date.getDate() + days)
    
    val newYear = date.getFullYear() as Int
    val newMonth = (date.getMonth() as Int) + 1
    val newDay = date.getDate() as Int
    
    return buildDateString(newYear, newMonth, newDay)
}

private fun getDaysInMonth(year: Int, month: Int): Int {
    return js("new Date(year, month, 0).getDate()") as Int
}

private fun getFirstDayOfMonth(year: Int, month: Int): Int {
    return js("new Date(year, month - 1, 1).getDay()") as Int
}

private fun getMonthName(month: Int): String {
    val months = listOf("January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December")
    return months.getOrElse(month - 1) { "" }
}

private fun formatDateForDisplay(dateString: String): String {
    val parts = dateString.split("-")
    if (parts.size != 3) return dateString
    val year = parts[0]
    val month = parts[1].toIntOrNull() ?: return dateString
    val day = parts[2]
    val monthName = getMonthName(month).take(3)
    return "$day $monthName $year"
}

private fun isDateDisabled(dateString: String, minDate: String?, maxDate: String?): Boolean {
    if (minDate != null && dateString < minDate) return true
    if (maxDate != null && dateString > maxDate) return true
    return false
}

private fun getSuggestedDates(): List<SuggestedDate> {
    val today = getTodayDate()
    return listOf(
        SuggestedDate(today, "Today"),
        SuggestedDate(addDays(today, 1), "Tomorrow"),
        SuggestedDate(addDays(today, 7), "Next Week"),
        SuggestedDate(addDays(today, 14), "In 2 Weeks"),
        SuggestedDate(addDays(today, 30), "Next Month")
    )
}
